import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  TablePagination,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { SwapVert } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faDownload,
  faPrint,
  faBarsStaggered,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import HTMLDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import DocumentPreview from "../../dialogs/DocumentPreviewDialog";

const HistoryTable = ({
  toggleSidebar,
  setApplicationToEdit,
  setIsHistoryClicked,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [openDocPreview, setOpenDocPreview] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const applicationTitles = useSelector((state) => state.applicationTitles);
  const { titles } = applicationTitles;

  // Handle sorting logic
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  // Apply category filter
  const categoryFilteredData =
    selectedCategory && selectedCategory !== "all"
      ? titles?.filter((item) => item.Category === selectedCategory)
      : titles;

  const sortedData =
    categoryFilteredData &&
    [...categoryFilteredData?.filter((title) => title?.Title !== null)]?.sort(
      (a, b) => {
        const isAsc = sortConfig.direction === "asc";

        if (sortConfig.key === "timecreated") {
          // Handle date sorting using Moment.js
          const dateA = moment(a.timecreated, "DD-MM-YYYY");
          const dateB = moment(b.timecreated, "DD-MM-YYYY");

          // Handle invalid dates gracefully
          if (!dateA.isValid() || !dateB.isValid()) return 0;

          return isAsc ? dateA - dateB : dateB - dateA;
        }

        // Default sorting for other keys
        if (sortConfig.key === "Title") {
          return isAsc
            ? a.Title.localeCompare(b.Title)
            : b.Title.localeCompare(a.Title);
        }

        return 0; // If no valid sort key, return 0 (no sorting)
      }
    );

  // Handle search functionality
  const filteredData = sortedData?.filter((item) =>
    item?.Title.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Handle pagination
  const paginatedData = filteredData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const cleanQuillOutput = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll(".ql-align-center, .ql-align-right").forEach((el) => {
      if (el.classList.contains("ql-align-center")) {
        el.style.textAlign = "center";
        el.classList.remove("ql-align-center");
      }
      if (el.classList.contains("ql-align-right")) {
        el.style.textAlign = "right";
        el.classList.remove("ql-align-right");
      }
    });
    return div.innerHTML;
  };

  const handleDownload = () => {
    const cleanedHtml = cleanQuillOutput(selectedItem.Document);
    const docxBlob = HTMLDocx.asBlob(cleanedHtml);
    saveAs(
      docxBlob,
      `${selectedItem.Title || "Petition"} ${moment().format(
        "DD-MM-YYYY"
      )} .docx`
    );
  };

  const handlePrint = (petition) => {
    const printContent = cleanQuillOutput(petition);

    // Create a hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    // Load the print content into the iframe
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Print</title>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    iframeDoc.close();

    // Trigger the print dialog
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    // Ensure the iframe is cleared and removed after printing
    iframe.contentWindow.onafterprint = () => {
      iframeDoc.body.innerHTML = ""; // Clear content
      document.body.removeChild(iframe); // Remove the iframe
    };
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const copyLink = (item) => {
    const link = `${window.location.origin}/app/application-drafting/${item.chat_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: { xs: "100%", lg: "100%" },
        height: "calc(100vh - 50px)",
        bgcolor: "#f5f5f5",
        boxSizing: "border-box",
        p: { xs: "8px", lg: "12px 0px 0px 12px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          boxSizing: "border-box",
          flexGrow: 1,
          bgcolor: "#fff",
          borderRadius: "8px",
          p: { xs: "8px", lg: "16px 16px 8px 16px" },
        }}
      >
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: { xs: 1, lg: 0 },
          }}
        >
          <IconButton
            size="small"
            sx={{ display: { xs: "flex", lg: "none" } }}
            onClick={toggleSidebar}
          >
            <FontAwesomeIcon icon={faBarsStaggered} fontSize="medium" />
          </IconButton>
          <TextField
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
              maxWidth: "400px",
              mt: { xs: 0, lg: 1 },
              mb: { xs: 0, lg: 2 },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ccc",
                },
                "&:hover fieldset": {
                  borderColor: "#00509e",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#00509e",
                },
              },
              "& input": {
                padding: "10px",
                fontSize: "0.875rem",
              },
            }}
            placeholder="Search by title"
            value={searchTerm}
            onChange={(e) => {
              setPage(0);
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#303f9f", }} />
              ),
              sx: {
                pl: 1,
                backgroundColor: "#ffffff",
                borderRadius: "20px",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                display: { xs: "none", md: "block" },
                marginRight: "4px",
                fontSize: "13px",
                color: "#555",
                fontWeight: "500",
              }}
              htmlFor="resultsCount"
            >
              Filter by Type:
            </InputLabel>
            <FormControl
              variant="outlined"
              sx={{
                width: { xs: 80, lg: "unset" },
                minWidth: 80,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  height: { xs: "36px", lg: "30px" },
                  padding: "8px",
                },
                "& fieldset": {
                  borderColor: "#ddd",
                  borderWidth: "1px",
                },
                "& .MuiSelect-icon": {
                  right: "4px",
                },
                "& .MuiSelect-select": {
                  padding: "0px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <Select
                id="filterByCategory"
                value={selectedCategory}
                onChange={(e) => {
                  setPage(0);
                  handleCategoryChange(e.target.value);
                }}
                sx={{
                  fontSize: "13px",
                  height: "30px",
                  bgcolor: "#fff",
                }}
              >
                <MenuItem value={"all"} sx={{ fontSize: "0.875rem" }}>
                  All
                </MenuItem>

                {[
                  ...new Set(
                    titles
                      ?.filter((title) => title.Title !== null)
                      ?.map((title) => title.Category)
                  ),
                ].map((category) => (
                  <MenuItem
                    value={category}
                    key={category}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        <TableContainer
          component={Paper}
          sx={{
            mt: { xs: 2, lg: 0 },
            boxShadow: "none",
            borderRadius: "12px",
            overflowY: "auto",
            "::-webkit-scrollbar": {
              width: "5px",
            },
            "::-webkit-scrollbar-track": {
              background: "#fff",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#bbb",
              borderRadius: "6px",
            },
            "::-webkit-scrollbar:horizontal": {
              height: "4px",
            },
            "::-webkit-scrollbar-thumb:horizontal": {
              background: "#bbb",
              borderRadius: "6px",
            },
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#101936",
                    py: { xs: 1, lg: 2 },
                    fontSize: { xs: "13px", lg: "14px" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Title
                    <IconButton
                      onClick={() => handleSort("Title")}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <SwapVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#101936",
                    py: { xs: 1, lg: 2 },
                    fontSize: { xs: "13px", lg: "14px" },
                  }}
                >
                  Document Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#101936",
                    py: { xs: 1, lg: 2 },
                    fontSize: { xs: "13px", lg: "14px" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Last Updated
                    <IconButton
                      onClick={() => handleSort("timecreated")}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <SwapVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#101936",
                    py: { xs: 1, lg: 2 },
                    fontSize: { xs: "13px", lg: "14px" },
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.length > 0 ? (
                paginatedData?.map((item, index) => (
                  <TableRow
                    key={item.chat_id}
                    sx={{
                      fontSize: "13px",
                      backgroundColor: "white",
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
                      },
                      "&:not(:last-child)": {
                        borderBottom: "1px solid #f0f0f0",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        py: { xs: 1, lg: 2 },
                        fontSize: { xs: "13px", lg: "14px" },
                        color: "#101936",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "#3C50B1",
                        },
                      }}
                      onClick={() => {
                        setOpenDocPreview(true);
                        setSelectedItem(item);
                      }}
                    >
                      {truncateText(item.Title, 100)}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: { xs: 1, lg: 2 },
                        fontSize: { xs: "13px", lg: "14px" },
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "#3C50B1",
                        },
                      }}
                      onClick={() => {
                        setOpenDocPreview(true);
                        setSelectedItem(item);
                      }}
                    >
                      {item.Category}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: { xs: 1, lg: 2 },
                        fontSize: { xs: "13px", lg: "14px" },
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "#3C50B1",
                        },
                      }}
                      onClick={() => {
                        setOpenDocPreview(true);
                        setSelectedItem(item);
                      }}
                    >
                      {moment(item.timecreated, "DD-MM-YYYY").isValid()
                        ? moment(item.timecreated, "DD-MM-YYYY").format("LL")
                        : item.timecreated}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: { xs: 1, lg: 2 },
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{
                            color: "inherit",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                          onClick={(event) => {
                            setSelectedItem(item);
                            setAnchorEl(event.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          PaperProps={{
                            sx: {
                              boxShadow:
                                "0px 4px 12px rgba(0, 0, 0, 0.1) !important",
                              borderRadius: "8px",
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              setOpenDocPreview(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon
                                sx={{
                                  color: "#4CAF50",
                                  width: "15px",
                                  height: "auto",
                                }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Preview
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setApplicationToEdit(selectedItem);
                              setIsHistoryClicked(false);
                              setAnchorEl(null);
                              dispatch({
                                type: "SET_SELECTED_APPLICATION",
                                payload: selectedItem,
                              });
                            }}
                          >
                            <ListItemIcon>
                              <FontAwesomeIcon
                                size="xs"
                                icon={faPenToSquare}
                                style={{ color: "#2196f3" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Edit
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleDownload();
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon>
                              <FontAwesomeIcon
                                size="xs"
                                icon={faDownload}
                                style={{ color: "#ff9800" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Download
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handlePrint(selectedItem.Document);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon>
                              <FontAwesomeIcon
                                size="xs"
                                icon={faPrint}
                                style={{ color: "#d2042d" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Print
                            </Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography sx={{ fontSize: "14px" }}>
                      No records found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1,
            width: "100%",
            overflowY: "auto",
            "& .MuiTablePagination-root": {
              width: "100%",
              maxWidth: { xs: "100%", sm: "80%", md: "60%" },
            },
            "& .MuiTablePagination-toolbar": {
              p: 0,
              flexWrap: "wrap",
              justifyContent: "center",
            },
            "& .MuiSelect-select": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiInputBase-root": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            },
          }}
        >
          <TablePagination
            size="small"
            component="div"
            count={filteredData?.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
      <DocumentPreview
        open={openDocPreview}
        onClose={() => setOpenDocPreview(false)}
        content={selectedItem.Document}
        title={selectedItem.Title}
        onDownload={handleDownload}
        onPrint={() => handlePrint(selectedItem.Document)}
        onCopyLink={() => copyLink(selectedItem)}
      />
    </Box>
  );
};

export default HistoryTable;
